import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useGuardedRealtimeList from '../../../realtime/useGuardedRealtimeList';
import { replaceRealtimeSalesTargets } from '../../../redux/slices/salesTargetsSlice';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  Card,
  Alert,
  Spinner,
  Form,
  Row,
  Col,
  Accordion
} from 'react-bootstrap';
import {
  fetchSalesTargetsData,
  createSalesTargetRecord,
  updateSalesTargetRecord,
  setSearchTerm,
  setSelectedRepType,
  setSelectedYear,
  setAllAccordionsOpen,
  clearError,
  selectSalesTargetsData,
  selectSalesTargetsLoading,
  selectSalesTargetsError,
  selectSearchTerm,
  selectSelectedRepType,
  selectSelectedYear,
  selectAllAccordionsOpen,
  selectGroupedSalesTargets
} from '../../../redux/slices/salesTargetsSlice';
import { selectMappedRoles } from '../../../redux/slices/userSlice';
import { fetchDropdownValues } from '../../../redux/slices/dropdownSlice';
import useUserDropdowns from '../../../functions/useUserDropdowns';
import { usePermissions } from '../../../hooks/usePermissions';
import './SalesTargets.css';

const SalesTargets = ({ showSaveNotificationMessage }) => {
  const dispatch = useDispatch();
  
  // Redux state
  const salesTargetsData = useSelector(selectSalesTargetsData);
  const loading = useSelector(selectSalesTargetsLoading);
  const error = useSelector(selectSalesTargetsError);
  const searchTerm = useSelector(selectSearchTerm);
  const selectedRepType = useSelector(selectSelectedRepType);
  const selectedYear = useSelector(selectSelectedYear);
  const allAccordionsOpen = useSelector(selectAllAccordionsOpen);
  const groupedData = useSelector(selectGroupedSalesTargets);
  const mappedUserRoles = useSelector(selectMappedRoles);
  
  // Local state for accordion management
  const [activeAccordionKeys, setActiveAccordionKeys] = useState([]);
  
  // Track whether accordion state should be managed by the toggle button
  const [accordionStateManaged, setAccordionStateManaged] = useState(false);
  
  // Update accordion keys when allAccordionsOpen changes (from toggle button)
  useEffect(() => {
    if (accordionStateManaged) {
      if (allAccordionsOpen) {
        // Open all accordions
        const allKeys = groupedData.map((_, index) => index.toString());
        setActiveAccordionKeys(allKeys);
      } else {
        // Close all accordions
        setActiveAccordionKeys([]);
      }
      // Reset the flag after managing accordion state
      setAccordionStateManaged(false);
    }
  }, [allAccordionsOpen, groupedData, accordionStateManaged]);
  
  // Permissions
  const { canEdit } = usePermissions();
  const hasEditAccess = canEdit('apps.sales-targets') || mappedUserRoles?.Admin || mappedUserRoles?.Apps;
  
  // User dropdowns for rep selection
  const { salesOptions } = useUserDropdowns();
  
  // Get dropdown options from Redux
  const { options } = useSelector((state) => state.dropdown);
  
  // Market dropdown options
  const marketOptions = useMemo(() => 
    options?.filter(option => option.DropdownType === "Market")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [],
    [options]
  );
  
  // Helper function to create initial markets object from dropdown options
  const createInitialMarketsState = useCallback(() => {
    const marketsObj = {};
    marketOptions.forEach(option => {
      marketsObj[option.DisplayText] = true;
    });
    return marketsObj;
  }, [marketOptions]);
  
  // Local state for Add New form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    repType: '',
    salesRep: '',
    year: new Date().getFullYear(),
    markets: {}
  });
  
  // Initialize markets when marketOptions are loaded
  useEffect(() => {
    if (marketOptions.length > 0 && Object.keys(addForm.markets).length === 0) {
      setAddForm(prev => ({
        ...prev,
        markets: createInitialMarketsState()
      }));
    }
  }, [marketOptions, addForm.markets, createInitialMarketsState]);
  
  // Local state for inline editing
  const [editingCell, setEditingCell] = useState({ id: null, field: null });
  const [editValue, setEditValue] = useState('');
  useGuardedRealtimeList({ apiKey: 'bookingSalesTarget', blocked: loading || editingCell.id !== null,
    apply: rows => dispatch(replaceRealtimeSalesTargets(rows)) });
  
  // Track if we're in Tab navigation mode to handle notifications appropriately
  const [isTabNavigating, setIsTabNavigating] = useState(false);
  
  // Reset tab navigation state when editing cell changes (not during Tab navigation)
  useEffect(() => {
    if (!isTabNavigating && editingCell.id === null) {
      setIsTabNavigating(false);
    }
  }, [editingCell.id, isTabNavigating]);
  
  // Available years (current year minus 2 to next year, hiding older years 2020-2022)
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    
    // Add years from 2 years ago to next year
    for (let year = currentYear - 2; year <= currentYear + 1; year++) {
      years.push(year);
    }
    
    return years.reverse(); // Show newest years first
  }, []);
  
  // Month names for table headers
  const monthNames = useMemo(() => [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ], []);
  
  // Combined sales rep options based on rep type
  const availableSalesReps = useMemo(() => {
    // Calculate dropdown options inside useMemo to avoid dependency issues
    const bookingRepsOptions =
      options?.filter(option => option.DropdownType === "Booking Reps")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
      
    const additionalSalesOptions =
      options?.filter(option => option.DropdownType === "Booking Additional Sales")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

    let combined = [];
    if (addForm.repType === 'RSM') {
      combined = [...salesOptions, ...additionalSalesOptions.map(opt => ({
        displayName: opt.DisplayText,
        email: opt.DisplayText
      }))];
    } else if (addForm.repType === 'Rep') {
      combined = bookingRepsOptions.map(opt => ({
        displayName: opt.DisplayText,
        email: opt.DisplayText
      }));
    }
    
    // Sort the final combined array alphabetically by displayName
    return combined.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [addForm.repType, salesOptions, options]);

  // Separate dropdown options for form rendering
  const repTypeOptions =
    options?.filter(option => option.DropdownType === "Booking Rep Type")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  
  // Check which markets are already taken for current selection
  const unavailableMarkets = useMemo(() => {
    if (!addForm.salesRep || !addForm.year) return [];
    
    return salesTargetsData
      .filter(record => 
        record.SalesRep === addForm.salesRep && 
        record.Year === addForm.year
      )
      .map(record => record.Market?.toUpperCase() || ''); // Normalize to uppercase for comparison
  }, [salesTargetsData, addForm.salesRep, addForm.year]);
  
  // Fetch data when component mounts
  useEffect(() => {
    dispatch(fetchSalesTargetsData());
    dispatch(fetchDropdownValues());
  }, [dispatch]);
  
  // Handle search term change
  const handleSearchChange = useCallback((e) => {
    dispatch(setSearchTerm(e.target.value));
  }, [dispatch]);
  
  // Handle rep type filter change
  const handleRepTypeFilterChange = useCallback((repType) => {
    dispatch(setSelectedRepType(repType));
  }, [dispatch]);
  
  // Handle year filter change
  const handleYearFilterChange = useCallback((year) => {
    dispatch(setSelectedYear(year));
  }, [dispatch]);
  
  // Handle toggle all accordions
  const handleToggleAllAccordions = useCallback(() => {
    setAccordionStateManaged(true); // Flag that accordion state should be managed by toggle
    dispatch(setAllAccordionsOpen(!allAccordionsOpen));
  }, [dispatch, allAccordionsOpen]);
  
  // Handle Add New form submission
  const handleAddSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const selectedMarkets = Object.entries(addForm.markets)
      .filter(([market, selected]) => selected)
      .map(([market]) => market);
    
    if (selectedMarkets.length === 0) {
      alert('Please select at least one market');
      return;
    }
    
    try {
      for (const market of selectedMarkets) {
        const recordData = {
          salesRep: addForm.salesRep,
          year: addForm.year,
          market: market,
          salesRepType: addForm.repType,
          january: null,
          february: null,
          march: null,
          april: null,
          may: null,
          june: null,
          july: null,
          august: null,
          september: null,
          october: null,
          november: null,
          december: null
        };
        
        await dispatch(createSalesTargetRecord(recordData)).unwrap();
      }
      
      // Reset form and hide add form
      setAddForm({
        repType: '',
        salesRep: '',
        year: new Date().getFullYear(),
        markets: createInitialMarketsState()
      });
      setShowAddForm(false);
      
      // Refresh data
      dispatch(fetchSalesTargetsData());
      
      // Show success notification
      const marketCount = selectedMarkets.length;
      const marketText = marketCount === 1 ? 'market' : 'markets';
      showSaveNotificationMessage(`Successfully created sales targets for ${marketCount} ${marketText}`);
    } catch (error) {
      console.error('Error creating sales target records:', error);
      showSaveNotificationMessage(`Failed to create sales target records: ${error.message || error}`);
    }
  }, [dispatch, addForm, showSaveNotificationMessage, createInitialMarketsState]);
  
  // Check if a field can be edited
  const canEditField = useCallback((field, record, roles) => {
    // Admin and Apps can edit everything
    if (roles?.Admin || roles?.Apps) return true;
    
    // All month fields are editable for users with basic edit access
    const editableFields = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    return editableFields.includes(field);
  }, []);

  // Handle cell double click for editing
  const handleCellDoubleClick = useCallback((record, field) => {
    if (!hasEditAccess) return;
    
    // Check if field is editable based on permissions
    const isEditable = canEditField(field, record, mappedUserRoles);
    if (!isEditable) return;
    
    setEditingCell({ id: record.ID, field });
    setEditValue(record[field] || '');
  }, [hasEditAccess, mappedUserRoles, canEditField]);
  
  // Handle edit save
  // Handle edit save for Tab navigation (doesn't exit edit mode or show notification immediately)
  const handleTabSave = useCallback(async () => {
    if (!editingCell.id || !editingCell.field) return false;
    
    const record = salesTargetsData.find(r => r.ID === editingCell.id);
    if (!record) return false;
    
    // Get the current value from the record data
    const currentValue = record[editingCell.field] || '';
    
    // Convert both values to strings for comparison (handles null/undefined/empty cases)
    const currentValueStr = String(currentValue).trim();
    const editValueStr = String(editValue).trim();
    
    // If no change was made, just return success without API call
    if (currentValueStr === editValueStr) {
      return true;
    }
    
    try {
      await dispatch(updateSalesTargetRecord({
        id: editingCell.id,
        field: editingCell.field,
        value: editValue ? parseFloat(editValue) : null
      })).unwrap();
      
      // Don't exit edit mode or show notification immediately - Tab navigation will handle this
      return true;
    } catch (error) {
      console.error('Error updating field:', error);
      showSaveNotificationMessage(`Failed to update field: ${error.message || 'Unknown error'}`);
      return false;
    }
  }, [dispatch, editingCell, editValue, salesTargetsData, showSaveNotificationMessage]);

  const handleEditSave = useCallback(async () => {
    if (!editingCell.id || !editingCell.field) return;
    
    const record = salesTargetsData.find(r => r.ID === editingCell.id);
    if (!record) return;
    
    // Get the current value from the record data
    const currentValue = record[editingCell.field] || '';
    
    // Convert both values to strings for comparison (handles null/undefined/empty cases)
    const currentValueStr = String(currentValue).trim();
    const editValueStr = String(editValue).trim();
    
    // If no change was made, just exit edit mode without API call
    if (currentValueStr === editValueStr) {
      setEditingCell({ id: null, field: null });
      setEditValue('');
      return;
    }
    
    try {
      await dispatch(updateSalesTargetRecord({
        id: editingCell.id,
        field: editingCell.field,
        value: editValue ? parseFloat(editValue) : null
      })).unwrap();
      
      setEditingCell({ id: null, field: null });
      setEditValue('');
      
      // Show success notification
      showSaveNotificationMessage(`Successfully updated ${editingCell.field} value`);
    } catch (error) {
      console.error('Error updating field:', error);
      showSaveNotificationMessage(`Failed to update field: ${error.message || 'Unknown error'}`);
    }
  }, [dispatch, editingCell, editValue, salesTargetsData, showSaveNotificationMessage]);
  
  // Handle edit cancel
  const handleEditCancel = useCallback(() => {
    setEditingCell({ id: null, field: null });
    setEditValue('');
  }, []);
  
  // Handle Enter key to save, Escape to cancel
  // Find the next editable cell for Tab navigation
  const findNextEditableCell = useCallback((currentRecordId, currentField) => {
    const currentMonthIndex = monthNames.indexOf(currentField);
    
    // If not a month field, don't navigate
    if (currentMonthIndex === -1) return null;
    
    // Find current group and record
    let currentGroupIndex = -1;
    let currentRecordIndex = -1;
    
    for (let groupIdx = 0; groupIdx < groupedData.length; groupIdx++) {
      const recordIdx = groupedData[groupIdx].records.findIndex(r => r.ID === currentRecordId);
      if (recordIdx !== -1) {
        currentGroupIndex = groupIdx;
        currentRecordIndex = recordIdx;
        break;
      }
    }
    
    if (currentGroupIndex === -1) return null;
    
    const currentGroup = groupedData[currentGroupIndex];
    const currentRecord = currentGroup.records[currentRecordIndex];
    
    // Try next month in same row
    if (currentMonthIndex < monthNames.length - 1) {
      const nextMonth = monthNames[currentMonthIndex + 1];
      if (canEditField(nextMonth, currentRecord, mappedUserRoles)) {
        return { recordId: currentRecordId, field: nextMonth };
      }
    }
    
    // Try first month of next row in same group
    if (currentRecordIndex < currentGroup.records.length - 1) {
      const nextRecord = currentGroup.records[currentRecordIndex + 1];
      for (const month of monthNames) {
        if (canEditField(month, nextRecord, mappedUserRoles)) {
          return { recordId: nextRecord.ID, field: month };
        }
      }
    }
    
    // Try first record of next group
    if (currentGroupIndex < groupedData.length - 1) {
      const nextGroup = groupedData[currentGroupIndex + 1];
      if (nextGroup.records.length > 0) {
        const firstRecord = nextGroup.records[0];
        for (const month of monthNames) {
          if (canEditField(month, firstRecord, mappedUserRoles)) {
            return { recordId: firstRecord.ID, field: month };
          }
        }
      }
    }
    
    return null;
  }, [monthNames, groupedData, canEditField, mappedUserRoles]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      setIsTabNavigating(true);
      
      // Save current value first using Tab-specific save function
      handleTabSave().then(saveSuccessful => {
        if (saveSuccessful) {
          // Find and move to next editable cell
          const nextCell = findNextEditableCell(editingCell.id, editingCell.field);
          if (nextCell) {
            // Set new editing cell
            setEditingCell({ id: nextCell.recordId, field: nextCell.field });
            
            // Get the current value for the next cell
            const targetRecord = groupedData
              .flatMap(group => group.records)
              .find(record => record.ID === nextCell.recordId);
            
            if (targetRecord) {
              setEditValue(targetRecord[nextCell.field]?.toString() || '');
            }
            
            // Show notification for successful tab navigation after brief delay
            setTimeout(() => {
              setIsTabNavigating(false);
              if (!isTabNavigating) { // Only show if not rapidly tabbing
                showSaveNotificationMessage(`Field updated - navigated to next cell`);
              }
            }, 200);
          } else {
            // No next cell found, exit edit mode and show notification
            setEditingCell({ id: null, field: null });
            setEditValue('');
            setIsTabNavigating(false);
            showSaveNotificationMessage(`Successfully updated ${editingCell.field} value - end of table`);
          }
        } else {
          // Save failed, stay in current cell
          setIsTabNavigating(false);
        }
      });
    }
  }, [handleEditSave, handleEditCancel, findNextEditableCell, editingCell, groupedData, handleTabSave, showSaveNotificationMessage, isTabNavigating]);
  
  // Format currency values
  const formatCurrency = useCallback((value) => {
    if (!value) return '$0.00';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue || 0);
  }, []);
  
  // Calculate total for a group (all records across all months)
  const calculateGroupTotal = useCallback((group) => {
    let total = 0;
    const monthFields = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    
    console.log('Calculating total for group:', group.salesRep, group.year);
    console.log('Group records:', group.records);
    
    group.records.forEach(record => {
      console.log('Processing record:', record.Market, record);
      monthFields.forEach(month => {
        const value = record[month];
        console.log(`Month ${month}:`, value);
        if (value && !isNaN(value)) {
          const numValue = parseFloat(value);
          total += numValue;
          console.log(`Added ${numValue} to total, new total: ${total}`);
        }
      });
    });
    
    console.log('Final total:', total);
    return total;
  }, []);

  // Calculate total for a single row (all months for one market record)
  const calculateRowTotal = useCallback((record) => {
    let total = 0;
    const monthFields = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    
    monthFields.forEach(month => {
      const value = record[month];
      if (value && !isNaN(value)) {
        const numValue = parseFloat(value);
        total += numValue;
      }
    });
    
    return total;
  }, []);
  
  // Render editable cell content (without the td wrapper)
  const renderEditableCell = useCallback((record, field, value, displayValue) => {
    const isEditing = editingCell.id === record.ID && editingCell.field === field;
    
    if (isEditing) {
      return (
        <input
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleEditSave}
          onKeyDown={handleKeyDown}
          autoFocus
          className="form-control form-control-sm"
          style={{ minWidth: '80px' }}
        />
      );
    }
    
    return displayValue || value || '$0';
  }, [editingCell, editValue, handleEditSave, handleKeyDown]);
  
  // Render complete table cell with double-click functionality  
  const renderTableCell = useCallback((record, field, value, displayValue, className = '') => {
    const canEdit = canEditField(field, record, mappedUserRoles);
    const isEditing = editingCell.id === record.ID && editingCell.field === field;
    const formattedValue = value ? formatCurrency(value) : '$0';
    
    // Build className with editing state
    let cellClassName = className;
    if (canEdit) {
      cellClassName += ' editable-cell';
    }
    if (isEditing) {
      cellClassName += ' editing-cell';
    }
    
    return (
      <td
        key={field}
        data-field={field}
        className={cellClassName.trim()}
        onDoubleClick={() => canEdit && handleCellDoubleClick(record, field)}
        title={canEdit ? 'Double-click to edit' : ''}
        style={{ cursor: canEdit ? 'pointer' : 'default' }}
      >
        {renderEditableCell(record, field, value, displayValue || formattedValue)}
      </td>
    );
  }, [
    canEditField,
    mappedUserRoles, 
    editingCell,
    handleCellDoubleClick,
    renderEditableCell,
    formatCurrency
  ]);
  
  if (loading && salesTargetsData.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  
  return (
    <div className="sales-targets-container">
        <Card>
          <Card.Header className="sales-targets-header">
            <h5 className="mb-0">Sales Targets</h5>
            
            <div className="d-flex gap-3 align-items-center">
              {hasEditAccess && (
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={() => setShowAddForm(!showAddForm)}
                >
                  <i className="bi bi-plus me-1"></i>
                  {showAddForm ? 'Cancel' : 'Add New'}
                </Button>
              )}
            </div>
          </Card.Header>
          
          <Card.Body>
            {/* Add New Form */}
            {showAddForm && (
              <Card className="mb-3 border-success">
                <Card.Header className="bg-success text-white">
                  <h6 className="mb-0">Add New Sales Targets</h6>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleAddSubmit}>
                    <Row className="g-3 sales-targets-add-grid">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small">Rep Type *</Form.Label>
                          <Form.Select
                            size="sm"
                            required
                            value={addForm.repType}
                            onChange={(e) => setAddForm(prev => ({
                              ...prev,
                              repType: e.target.value,
                              salesRep: '' // Reset sales rep when rep type changes
                            }))}
                          >
                            <option value="">Select Rep Type...</option>
                            {repTypeOptions.map(option => (
                              <option key={option.ID} value={option.DisplayText}>
                                {option.DisplayText}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small">Sales Rep *</Form.Label>
                          <Form.Select
                            size="sm"
                            required
                            value={addForm.salesRep}
                            onChange={(e) => setAddForm(prev => ({
                              ...prev,
                              salesRep: e.target.value
                            }))}
                            disabled={!addForm.repType}
                          >
                            <option value="">Select Sales Rep...</option>
                            {availableSalesReps.map(rep => (
                              <option key={rep.email} value={rep.displayName}>
                                {rep.displayName}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small">Year *</Form.Label>
                          <Form.Select
                            size="sm"
                            required
                            value={addForm.year}
                            onChange={(e) => setAddForm(prev => ({
                              ...prev,
                              year: parseInt(e.target.value)
                            }))}
                          >
                            {availableYears.map(year => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4} className="sales-targets-markets-field">
                        <Form.Group>
                          <Form.Label className="small">Markets *</Form.Label>
                          <div className="sales-targets-market-options">
                            {marketOptions.map(option => {
                              const marketName = option.DisplayText;
                              const isUnavailable = unavailableMarkets.includes(marketName.toUpperCase());
                              const isChecked = addForm.markets[marketName] || false;
                              
                              return (
                                <Form.Check
                                  key={option.ID}
                                  type="checkbox"
                                  id={`market-${option.ID}`}
                                  label={marketName}
                                  checked={isChecked && !isUnavailable}
                                  disabled={isUnavailable}
                                  onChange={(e) => setAddForm(prev => ({
                                    ...prev,
                                    markets: {
                                      ...prev.markets,
                                      [marketName]: e.target.checked
                                    }
                                  }))}
                                />
                              );
                            })}
                          </div>
                          {unavailableMarkets.length > 0 && (
                            <Form.Text className="text-muted">
                              <i className="bi bi-info-circle me-1"></i>
                              Some markets are disabled because records already exist.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mt-3">
                      <Col>
                        <div className="sales-targets-form-actions">
                          <Button 
                            type="submit" 
                            variant="success"
                            size="sm"
                            disabled={!addForm.repType || !addForm.salesRep || Object.values(addForm.markets).every(v => !v)}
                          >
                            Add Sales Targets
                          </Button>
                          <Button 
                            type="button"
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => setShowAddForm(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            )}
            
            {/* Filters */}
            <Row className="mb-3 sales-targets-filter-grid">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small">Search</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Search sales rep or market..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small">Rep Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={selectedRepType}
                    onChange={(e) => handleRepTypeFilterChange(e.target.value)}
                  >
                    <option value="all">All Rep Types</option>
                    {repTypeOptions.map(option => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small">Year</Form.Label>
                  <ButtonGroup size="sm" className="w-100 sales-targets-year-buttons">
                    {availableYears.map(year => (
                      <Button
                        key={year}
                        variant={selectedYear === year ? 'primary' : 'outline-primary'}
                        onClick={() => handleYearFilterChange(year)}
                      >
                        {year}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small">&nbsp;</Form.Label>
                  <div>
                    <Button
                      className="sales-targets-toggle-all"
                      size="sm"
                      variant="outline-secondary"
                      onClick={handleToggleAllAccordions}
                    >
                      {allAccordionsOpen ? 'Close All' : 'Open All'}
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>
            
            {error && (
              <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
                Error: {error}
              </Alert>
            )}
            
            {/* Sales Targets Accordions */}
            <div className="activity-log-table-container">
              <Accordion 
                activeKey={activeAccordionKeys}
                onSelect={(eventKey) => {
                  if (eventKey === null) return;
                  
                  if (activeAccordionKeys.includes(eventKey)) {
                    // Remove the key (close accordion)
                    setActiveAccordionKeys(activeAccordionKeys.filter(key => key !== eventKey));
                  } else {
                    // Add the key (open accordion)
                    setActiveAccordionKeys([...activeAccordionKeys, eventKey]);
                  }
                }}
                className="sales-targets-accordion"
              >
              {groupedData.length === 0 ? (
                <div className="text-center text-muted py-4">
                  No sales targets found matching the current filters
                </div>
              ) : (
                groupedData.map((group, index) => {
                  const groupTotal = calculateGroupTotal(group);
                  return (
                    <Accordion.Item eventKey={index.toString()} key={`${group.year}-${group.salesRep}-${group.salesRepType}`}>
                      <Accordion.Header>
                        {group.year} - {group.salesRep} ({group.salesRepType}) - {formatCurrency(groupTotal)}
                      </Accordion.Header>
                    <Accordion.Body>
                      <div className="enhanced-table-wrapper">
                        <div className="table-responsive">
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th data-field="Market">Market</th>
                                {monthNames.map(month => (
                                  <th key={month} data-field={month}>{month}</th>
                                ))}
                                <th data-field="Total">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.records.map(record => (
                                <tr key={record.ID}>
                                  <td data-field="Market" className="market-cell">{record.Market}</td>
                                  {monthNames.map(month => 
                                    renderTableCell(record, month, record[month])
                                  )}
                                  <td data-field="Total" className="total-cell" style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                                    {formatCurrency(calculateRowTotal(record))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                  );
                })
              )}
            </Accordion>
            </div>
          </Card.Body>
        </Card>
    </div>
  );
};

SalesTargets.propTypes = {
  showSaveNotificationMessage: PropTypes.func.isRequired,
};

export default SalesTargets;
